import { getAllMetricDefinitions, getEnabledMetricDefinitions } from './getMetricDefinitions';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { MetricsConfigColumnDefinition } from '@/types/MetricsConfigColumnDefinition';

const logger = useLogger('getSavedMetricsConfig.ts');

export async function getSavedMetricsConfig(): Promise<MetricsConfigColumnDefinition[]> {
    const [ allMetrics, enabledMetrics ] = await Promise.all([ getAllMetricDefinitions(), getEnabledMetricDefinitions() ]);
    const enabledMetricsMap = new Map(enabledMetrics.map((m) => [ `${m.category}.${m.subcategory}.${m.id}`, m ]));
    const metrics = allMetrics.map((m) => {
        const enabledMetric = enabledMetricsMap.get(`${m.category}.${m.subcategory}.${m.id}`);
        return {
            isEnabled: Boolean(enabledMetric),
            ...(enabledMetric?.customName && { customName: enabledMetric?.customName }),
            ...m
        };
		
    });

    const result = transformFlatToNestedMetrics(metrics);
    logger.debug({ result });
    return result.metricsConfig as MetricsConfigColumnDefinition[];
}

// TODO: Code to transform to old format. We should just eventually use the flatter format from the API.
// The flatter format is simpler. In the meantime we just transform to old format to minimise disruption.
export type FlatMetric = {
	id: string;
	defaultName?: string | undefined;
	customName?: string | undefined;
	dataType: string;
	category: string;
	subcategory?: string | undefined;
	isEnabled?: boolean;
};

export type NestedMetric = {
	headerName: string;
	dataType?: string;
	field: string;
	isSelected?: boolean;
	children: NestedMetric[];
	parent?: { headerName: string; parent?: { headerName: string } };
	label?: string;
};

export type NestedCategory = {
	headerName: string;
	field?: string;
	children: NestedMetric[];
	parent?: { headerName: string };
};

type MetricsConfig = { metricsConfig: NestedCategory[] };

export function transformFlatToNestedMetrics(input: FlatMetric[]): MetricsConfig {
    const result: MetricsConfig = { metricsConfig: [] };
    const categoryMap: Record<string, NestedCategory> = {};

    // eslint-disable-next-line complexity
    input.forEach((item) => {
        const category = item.category;
        const subcategory = item.subcategory;

        if (!categoryMap[category]) {
            categoryMap[category] = {
                headerName: category,
                children: []
            };
            result.metricsConfig.push(categoryMap[category]);
        }

        let subcategoryObj = categoryMap[category].children.find(
            (child) => child.headerName === subcategory
        );

        if (!subcategoryObj) {
            subcategoryObj = {
                headerName: subcategory ?? '',
                field: subcategory?.replace(/\s+/g, '').toLowerCase() ?? '',
                children: [],
                parent: { headerName: category }
            };
            categoryMap[category].children.push(subcategoryObj);
        }

        const metric: NestedMetric = {
            headerName: item.defaultName ?? '',
            label: item.customName,
            dataType: item.dataType,
            field: item.id,
            isSelected: Boolean(item.isEnabled),
            children: [],
            parent: {
                headerName: subcategory ?? '',
                parent: { headerName: category }
            }
        };

        subcategoryObj.children.push(metric);
    });

    return result;
}

export function transformNestedToFlatMetrics(output: MetricsConfig): FlatMetric[] {
    const flatMetrics: FlatMetric[] = [];

    function traverse(category: string, subcategory: string, metrics: NestedMetric[]) {
        metrics.forEach((metric) => {
            flatMetrics.push({
                id: metric.field,
                defaultName: metric.headerName,
                customName: metric.label,
                isEnabled: Boolean(metric.isSelected),
                dataType: metric.dataType ?? '',
                category,
                subcategory
            });

            // Recursively traverse any children (though your current example doesn't have children inside individual metrics)
            if (metric.children.length > 0) {
                traverse(category, metric.headerName, metric.children);
            }
        });
    }

    output.metricsConfig.forEach((category) => {
        category.children.forEach((subcategory) => {
            traverse(category.headerName, subcategory.headerName, subcategory.children);
        });
    });

    return flatMetrics;
}